"use client";

import {
  type ChangeEvent,
  type InputHTMLAttributes,
  type MouseEventHandler,
  type Ref,
  useRef,
  useState,
  type FC,
} from "react";
import { mergeClasses } from "@huub-web/utils";
import { useTranslation } from "react-i18next";

import { BaseInputField, type BaseInputFieldProps } from "../BaseInputField";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { mergeRefs } from "../../utils/mergeRefs";
import type { ValidatableProps } from "../../props";
import { useFormValidation } from "../../hooks/useFormValidation";

import { inputStyle } from "../BaseInputField/BaseInputField.css";
import { typography } from "../../theme/css/typography.css";

export type FileUploadFieldProps = Omit<
  BaseInputFieldProps,
  "trailingLast" | "placeholder"
> &
  Pick<
    InputHTMLAttributes<HTMLInputElement>,
    "placeholder" | "accept" | "multiple"
  > &
  ValidatableProps & {
    onChange?: FileUploadFieldChangeHandler;
    inputRef?: Ref<HTMLInputElement>;
  };

export type FileUploadFieldChangeHandler = (
  files: FileList | null,
  e?: ChangeEvent<HTMLInputElement>,
) => void;

/**
 * FileUploadField is a component that wraps around the base input field with a file input.
 */
export const FileUploadField: FC<FileUploadFieldProps> = ({
  id: idProp,
  error: errorProp,
  disabled: disabledProp,
  placeholder,
  accept,
  multiple,
  variant = "default",
  width = "stretch",
  inputRef,
  align,
  validation,
  name,
  className,
  onChange,
  onClick,
  ref,
  ...props
}) => {
  const { t } = useTranslation();
  const internalInputRef = useRef<HTMLInputElement>(null);

  const [files, setFiles] = useState<File[]>([]);

  const handleClick: MouseEventHandler<HTMLInputElement> = (e) => {
    internalInputRef.current?.click();
    onClick?.(e);
  };

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    onChange?.(e.target.files, e);
    setFiles(
      e.target.files && e.target.files.length > 0
        ? Array.from(e.target.files)
        : [],
    );
  };

  const { id, error, disabled } = useFormValidation(files && files.length > 0, {
    id: idProp,
    error: errorProp,
    disabled: disabledProp,
    validation,
    name,
    ref: internalInputRef,
  });

  const parsedPlaceholder = placeholder ? placeholder : t("general.selectFile");

  return (
    <BaseInputField
      ref={ref}
      onClick={handleClick}
      disabled={disabled}
      variant={variant}
      align={align}
      width={width}
      error={error}
      className={className}
      trailingLast={
        variant === "default" || variant === "opaque" ? (
          <Icon icon="upload" size="s2" />
        ) : undefined
      }
      proxy={
        <input
          id={id}
          ref={mergeRefs(internalInputRef, inputRef)}
          className={mergeClasses(
            typography.bodyS,
            inputStyle({ proxy: true, disabled }),
            className,
          )}
          disabled={disabled}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleChange}
          autoComplete="off"
          data-1p-ignore
        />
      }
      placeholder={files && files?.length > 0 ? undefined : parsedPlaceholder}
      {...props}
    >
      {files && files?.length > 0 && (
        <Text ellipsis>{files.map((file) => file.name)}</Text>
      )}
    </BaseInputField>
  );
};
